import { useCallback, useEffect, useState, type ReactNode } from "react";

import { consentEvidence, type ConsentEntry, type RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  Group,
  InfoNote,
  MarkControl,
  MarketShell,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Consent` — CUS-04's history, which is the one operation behind this screen and is not a list of
 * switches.
 *
 * The measurement grades this screen **buildable**, and the reason is worth stating because it is
 * the opposite of most of this tranche: `GET /v1/tenants/{tenantId}/customers/{customerId}/consents`
 * publishes `purpose`, `purposeVersion`, `policyVersion`, `kind`, `sequence`, `supersedesGrantId`,
 * `evidenceReference`, `source`, `jurisdiction` and `recordedAt` — **every grant and withdrawal ever
 * recorded**, which is more than the design draws and not less.
 *
 * So whether a purpose is on is the `kind` of its latest entry, and the design's own sentence is the
 * contract rather than a note: _"Consent (CUS-04) is a page, not a checkbox; each toggle is a
 * versioned record with history."_
 *
 * ## The four rows are the history's purposes, not the design's four
 *
 * The design draws `Location while booking`, `Share trips with contacts`, `Offers and news` and
 * `Improve the service`. **CUS-04's `purpose` is a free string with no published vocabulary**, so
 * those four are sample values rather than an enumeration — and a screen that hard-coded them would
 * draw four rows for a tenant with three purposes and hide a fourth from a tenant with five. The
 * rows are therefore the purposes the history actually names, in the order they were first recorded,
 * and that is how a withdrawal of a purpose this build has never heard of still turns off.
 *
 * Two drawn elements have no source and are drawn as absences:
 *
 *   - **the glyph per row.** The design gives each purpose its own — `location_on`,
 *     `share_location`, `campaign`, `insights` — and nothing publishes one. Q-80's rule is to derive
 *     from the one enumerated field about what a thing is, and there is no such field here, so every
 *     row takes `lock`: the bundle's own glyph for privacy, which `13 WP12`'s checkout app bar uses.
 *     Matching on the purpose string was considered and rejected for Q-80's stated reason.
 *   - **the sentence under each row.** `Needed to find drivers`, `Only when you choose`,
 *     `Email and SMS`, `Anonymised usage` — a plain-language explanation of a purpose, which nothing
 *     publishes. Drawn as the absence, which is the treatment Q-88 records for `1 Where to`'s saved
 *     places.
 *
 * ## Three required fields with no vocabulary, and where this build's answers come from
 *
 * Both the grant and the withdrawal require `evidenceReference`, `source` and `jurisdiction`, all
 * free strings. `jurisdiction` comes from **the entry being changed** — a withdrawal of a grant is in
 * the same jurisdiction that grant was recorded under, and CUS-04 publishes it on every entry, so
 * nothing is chosen. The other two are this build's and are named in `rider-api.ts` beside the
 * other two values of this shape, and recorded as a design question.
 */

export interface ConsentProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly customerId: string;
  readonly onBack: () => void;
}

/** The bundle's own glyph for privacy, used for every row because nothing publishes one. */
const CONSENT_GLYPH = "lock";

/** One purpose, as the history leaves it. */
interface Purpose {
  readonly purpose: string;
  /** The latest entry for it, which is the one that decides whether it is on. */
  readonly latest: ConsentEntry;
  /** The first, which fixes the row's position so a toggle does not reorder the page. */
  readonly firstSequence: number;
}

/**
 * The history, collapsed to one entry per purpose.
 *
 * `sequence` rather than `recordedAt`, because CUS-04 publishes the sequence as the ordering and two
 * entries recorded in the same millisecond would otherwise be ordered by whichever the JSON happened
 * to list first. The rows are then ordered by each purpose's **first** sequence, so turning a switch
 * off does not move its row to the bottom of the page under the rider's thumb.
 */
export function latestPerPurpose(history: readonly ConsentEntry[]): readonly Purpose[] {
  const byPurpose = new Map<string, Purpose>();
  for (const entry of history) {
    const existing = byPurpose.get(entry.purpose);
    if (existing === undefined) {
      byPurpose.set(entry.purpose, {
        purpose: entry.purpose,
        latest: entry,
        firstSequence: entry.sequence,
      });
      continue;
    }
    byPurpose.set(entry.purpose, {
      purpose: entry.purpose,
      latest: entry.sequence > existing.latest.sequence ? entry : existing.latest,
      firstSequence: Math.min(existing.firstSequence, entry.sequence),
    });
  }
  return [...byPurpose.values()].sort((left, right) => left.firstSequence - right.firstSequence);
}

export function Consent(props: ConsentProps): ReactNode {
  const { api, tenantId, customerId } = props;

  const [history, setHistory] = useState<readonly ConsentEntry[] | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [changing, setChanging] = useState<string | undefined>(undefined);

  const load = useCallback(() => {
    setState({ kind: "loading" });
    api
      .consents({ tenantId, customerId })
      .then((answer) => {
        setHistory(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.consentRead])));
  }, [api, tenantId, customerId]);

  useEffect(load, [load]);

  const toggle = useCallback(
    (row: Purpose) => {
      setChanging(row.purpose);
      const at = new Date().toISOString();
      const done = (): void => {
        setChanging(undefined);
        load();
      };
      const failed = (error: unknown): void => {
        setChanging(undefined);
        setState(stateForError(error, [SCOPES.consentWrite]));
      };

      if (row.latest.kind === "grant") {
        /*
         * Withdrawing, which CUS-04 records as its own entry against the grant it supersedes.
         *
         * `grantId` is the grant's own `id` — the entry currently in force — and not the purpose.
         * CUS-04 answers 409 when that grant has already been withdrawn, which is the service
         * de-duplicating on its own record and is why no `Idempotency-Key` is sent: the contract
         * does not mark this idempotent and the 409 is the right answer rather than a replay.
         */
        api
          .withdrawConsent({
            tenantId,
            customerId,
            grantId: row.latest.id,
            evidenceReference: consentEvidence(at),
            jurisdiction: row.latest.jurisdiction,
          })
          .then(done)
          .catch(failed);
        return;
      }

      /*
       * Granting again, which needs the two versions the withdrawal carried.
       *
       * `purposeVersion` and `policyVersion` are required and are **not** this build's to choose:
       * they are on the entry being superseded, so re-granting a purpose re-grants the same version
       * of the same policy. A fresh value here would have recorded a rider as agreeing to a policy
       * nobody showed them.
       */
      api
        .grantConsent({
          tenantId,
          customerId,
          purpose: row.purpose,
          purposeVersion: row.latest.purposeVersion,
          policyVersion: row.latest.policyVersion,
          evidenceReference: consentEvidence(at),
          jurisdiction: row.latest.jurisdiction,
        })
        .then(done)
        .catch(failed);
    },
    [api, tenantId, customerId, load],
  );

  if (state.kind !== "ready" || history === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading what you have agreed to."
        barTitle="Privacy"
        barSub="Consent"
      />
    );
  }

  const purposes = latestPerPurpose(history);

  const rows: RowValue[] = purposes.map((row) => {
    const on = row.latest.kind === "grant";
    return {
      id: row.purpose,
      lead: CONSENT_GLYPH,
      title: row.purpose,
      subAbsent: "No description of this purpose is published",
      badge: { kind: on ? "done" : "closed", label: on ? "on" : "off" },
      dimmed: !on,
      control: (
        <MarkControl
          on={on}
          label={`${on ? "Withdraw" : "Agree to"} ${row.purpose}`}
          onPress={() => toggle(row)}
          {...(changing === undefined
            ? {}
            : { unavailable: `A change to ${changing} is in flight` })}
        />
      ),
    };
  });

  return (
    <MarketShell
      screen="consent"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Privacy"
      barSub="Consent"
      right={{
        glyph: "help",
        label: "Help",
        unavailable: "No design file draws a help sheet for this screen",
      }}
      badge={versionBadge(history)}
      title="What you've agreed to"
      sub="Withdrawing is as easy as agreeing."
      actions={[
        {
          label: "See consent history",
          glyph: "history",
          primary: false,
          /*
           * The history is already read — it is the whole of this screen's source — and there is
           * **no design for a screen that shows it**. The bundle draws thirty-two rider screens and
           * none of them is a consent history, so building one would be designing rather than
           * copying. The button is drawn, because the design draws it, and says that.
           */
          unavailable:
            "No design file draws a consent history screen, so there is nowhere published to go",
        },
      ]}
    >
      <Group rows={rows} empty={<NoConsents />} />
      <InfoNote text="Each change is recorded as its own version (CUS-04), with the date you made it." />
    </MarketShell>
  );
}

/**
 * `v3 · updated 1 Sep` — the version of what was agreed, from the latest entry in the whole history.
 *
 * The measurement names `history.policyVersion` for this, and the instant beside it is the same
 * entry's `recordedAt`. The latest entry across all purposes rather than per purpose, because the
 * chip is about the page.
 */
function versionBadge(history: readonly ConsentEntry[]): BadgeValue {
  const latest = [...history].sort((left, right) => right.sequence - left.sequence)[0];
  if (latest === undefined) return { kind: "unknown", label: "nothing recorded" };
  const updated = new Date(latest.recordedAt).toLocaleDateString([], {
    day: "numeric",
    month: "short",
  });
  return { kind: "done", label: `v${latest.policyVersion} · updated ${updated}` };
}

/**
 * The empty state, which on this screen is a fact rather than a failure.
 *
 * CUS-04 answering an empty history means this customer has never recorded a consent decision — not
 * that they have agreed to nothing by default, and not that the read failed. The words are this
 * build's; the design draws no empty state here.
 */
function NoConsents(): ReactNode {
  return (
    <div className="row">
      <span className="row__middle">
        <span className="row__title">Nothing recorded yet</span>
        <span className="row__sub">
          No consent has been granted or withdrawn on this account. CUS-04 holds no entry for it.
        </span>
      </span>
    </div>
  );
}
